import { Component, createRef } from "react";
import type { ComponentType, RefObject } from "react";

import style from "./style.module.css";

export interface SlidingToggleOption {
  id: string;
  label: string | ComponentType;
  onToggle?: () => void;
}

interface SlidingToggleProps {
  customStyles?: Record<string, string>;
  options?: SlidingToggleOption[];
  selectedOption?: string;
}

interface SlidingToggleState {
  selectedOption: string;
}

export class SlidingToggle extends Component<
  SlidingToggleProps,
  SlidingToggleState
> {
  wrapperRef: RefObject<HTMLDivElement | null>;

  constructor(props: SlidingToggleProps) {
    super(props);

    this.wrapperRef = createRef<HTMLDivElement>();

    this.state = {
      selectedOption: props.options?.[0]?.id ?? ""
    };
  }

  toggleSwitch(option: SlidingToggleOption) {
    this.setState({
      selectedOption: option.id
    });
    if (option.onToggle) {
      option.onToggle();
    }
  }

  componentDidMount() {
    this.defineStyleSheetVariableColours();
    if (this.props.selectedOption) {
      this.setState({
        selectedOption: this.props.selectedOption
      });
    }
  }

  componentDidUpdate(oldProps: SlidingToggleProps) {
    if (
      this.props.selectedOption &&
      this.props.selectedOption !== oldProps.selectedOption &&
      this.props.selectedOption !== this.state.selectedOption
    ) {
      this.setState({
        selectedOption: this.props.selectedOption
      });
    }
  }

  defineStyleSheetVariableColours() {
    const { customStyles = {} } = this.props;

    Object.keys(customStyles).forEach(key => {
      const variableKey = `--switch-${key}`;
      this.wrapperRef.current?.style.setProperty(
        variableKey,
        customStyles[key] ?? null
      );
    });
  }

  renderLabel(label: string | ComponentType) {
    if (typeof label === "string") {
      return label;
    } else {
      const LabelComponent = label;
      return <LabelComponent />;
    }
  }

  render() {
    const { options } = this.props;

    if (!options || !options.length) return null;

    const [leftOption, rightOption] = options;

    if (!leftOption || !rightOption) return null;

    const { selectedOption } = this.state;

    const isLeftSelected = selectedOption === leftOption.id;

    return (
      <div
        className={`${style.toggleWrapper} ${style.toggleVariables}`}
        ref={this.wrapperRef}
      >
        <div
          className={`${style.toggleSwitch} ${
            isLeftSelected ? style.toggleSwitchLeft : style.toggleSwitchRight
          }`}
        />
        <div
          className={style.toggle}
          onClick={() => this.toggleSwitch(leftOption)}
          data-toggle-left="true"
          data-is-active={isLeftSelected}
        >
          <div>{this.renderLabel(leftOption.label)}</div>
        </div>
        <div
          className={style.toggle}
          onClick={() => this.toggleSwitch(rightOption)}
          data-toggle-right="true"
          data-is-active={!isLeftSelected}
        >
          {this.renderLabel(rightOption.label)}
        </div>
      </div>
    );
  }
}

export default SlidingToggle;
